import type { RequestPolicy } from 'urql';

import { useIsLoggedIn } from '@order/AuthMachine';
import { DurationUnit, TierStatusName } from '@order/graphql';
import { DateDurationUnit, useLocalizationContext } from '@order/Localization';

import { useSweetpassTierQuery } from './graphql/SweetpassTier.generated';
import { getSweetpassTierNameLocalizationId } from './utils';

export const useSweetpassTier = (
  requestPolicy: RequestPolicy = 'cache-only',
) => {
  const isLoggedIn = useIsLoggedIn();
  const { formatDurationFromUnit } = useLocalizationContext();
  const [sweetpassTierResponse, refetchSweetpassTier] = useSweetpassTierQuery({
    requestPolicy,
    pause: !isLoggedIn,
  });

  const { t } = useLocalizationContext();

  const isSweetpassTierLoading = sweetpassTierResponse?.fetching;
  const hasUnknownTierError = Boolean(sweetpassTierResponse?.error);

  const loyaltyProfile = sweetpassTierResponse?.data?.loyaltyProfile;
  const loyaltyId = sweetpassTierResponse?.data?.loyaltyProfile?.loyaltyId;
  const joinDate = loyaltyProfile?.joinDate;

  const sweetpassTier = loyaltyProfile?.tier;
  const sweetpassTierStatus = loyaltyProfile?.tierStatus;
  const hasTrialMembership =
    sweetpassTierStatus?.name === TierStatusName.FreeTrial;

  const sweetpassTierName = sweetpassTier?.name;
  const formattedSweetpassTierName = t(
    getSweetpassTierNameLocalizationId(sweetpassTierName, hasTrialMembership),
  );

  const sweetpassNextTier = loyaltyProfile?.tier?.nextTier;
  const sweetpassNextTierId = sweetpassNextTier?.id;
  const sweetpassNextTierName = sweetpassNextTier?.name;

  const trialEligibility = loyaltyProfile?.trialEligible;
  const duration = trialEligibility?.[0]?.duration;
  const durationUnit = convertDurationUnit(
    trialEligibility?.[0]?.durationUnit ?? DurationUnit.Day,
  );
  const trialDuration = formatDurationFromUnit({ duration, durationUnit });
  const trialDurationSingular = formatDurationFromUnit({
    duration,
    durationUnit,
    singular: true,
  });
  const isEligibleForFreeTrial = Boolean(trialEligibility?.length);

  return {
    joinDate,
    loyaltyId,
    hasTrialMembership,
    sweetpassTierName,
    formattedSweetpassTierName,
    sweetpassNextTierId,
    sweetpassNextTierName,
    isSweetpassTierLoading,
    isEligibleForFreeTrial,
    trialDuration,
    trialDurationSingular,
    hasUnknownTierError,
    refetchSweetpassTier,
  };
};

function convertDurationUnit(durationUnit: DurationUnit): DateDurationUnit {
  if (durationUnit === DurationUnit.Day) return DateDurationUnit.Day;

  if (durationUnit === DurationUnit.Week) return DateDurationUnit.Week;

  if (durationUnit === DurationUnit.Month) return DateDurationUnit.Month;

  return DateDurationUnit.Year;
}
